import { useEffect, useId, useRef, useState } from 'react';

import { useSession } from '../hooks/useSession';
import {
  SUPPORT_MESSAGE_MAX_LENGTH,
  SUPPORT_SUBJECTS,
  sendSupportMessage,
} from '../lib/api';

/**
 * Contact Support.
 *
 * A message goes to Metered Mining, which sends the email. The browser never touches a
 * mail provider and never learns the support address -- that is configuration, and
 * putting it in a bundle would publish it to every scraper that reads JavaScript.
 *
 * ---------------------------------------------------------------------------
 * THE FORM VALIDATES FOR THE CUSTOMER'S BENEFIT, NOT FOR SAFETY.
 *
 * Everything here is checked again server-side -- the address, the category against an
 * allowlist, the message length. What this does is save a round trip and put the error
 * next to the field, which the server cannot do.
 *
 * ---------------------------------------------------------------------------
 * NO ORDER NUMBER, DELIBERATELY.
 *
 * This is reached from the footer of every page, and plenty of questions are not about
 * an order. Attaching one automatically belongs to a support entry point on the order
 * itself, which is not this.
 */

interface SupportModalProps {
  open: boolean;
  onClose: () => void;
}

type Status =
  | { state: 'editing' }
  | { state: 'sending' }
  | { state: 'sent' }
  | { state: 'failed'; reason: string };

const FAILURE_MESSAGE = "We couldn't send your message. Please try again.";

export default function SupportModal({ open, onClose }: SupportModalProps) {
  const { state: session } = useSession();

  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState(SUPPORT_SUBJECTS[0].value);
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<Status>({ state: 'editing' });
  const [showErrors, setShowErrors] = useState(false);

  const dialogRef = useRef<HTMLDivElement | null>(null);
  const headingId = useId();
  const emailId = useId();
  const subjectId = useId();
  const messageId = useId();

  /*
   * PREFILL FROM THE SESSION WE ALREADY HAVE.
   *
   * `useSession` holds the address the server reported for the current session; no new
   * endpoint was added to obtain it. It is prefilled and left EDITABLE -- someone may
   * want a reply at a different address, and support has no reason to force otherwise.
   * The server records the account address separately when the two differ, so nothing is
   * lost by allowing the change.
   */
  const sessionEmail = session.status === 'authenticated' ? session.customer.email : null;

  useEffect(() => {
    if (!open) return;

    setEmail(sessionEmail ?? '');
    setSubject(SUPPORT_SUBJECTS[0].value);
    setMessage('');
    setStatus({ state: 'editing' });
    setShowErrors(false);
  }, [open, sessionEmail]);

  // Escape closes, matching what a dialog is expected to do. Not while sending: the
  // request is already in flight and dismissing would leave the outcome unknown.
  useEffect(() => {
    if (!open) return undefined;

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && status.state !== 'sending') onClose();
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose, status.state]);

  // Focus moves into the dialog when it opens, so a keyboard user is not left behind on
  // the page underneath.
  useEffect(() => {
    if (!open) return;
    const focusable = dialogRef.current?.querySelector<HTMLElement>(
      'input, select, textarea, button',
    );
    focusable?.focus();
  }, [open, status.state]);

  if (!open) return null;

  const trimmedEmail = email.trim();
  const trimmedMessage = message.trim();

  const emailError = trimmedEmail === '' ? 'An email address is required.' : null;
  const messageError = trimmedMessage === '' ? 'A message is required.' : null;
  const tooLong = trimmedMessage.length > SUPPORT_MESSAGE_MAX_LENGTH;

  const sending = status.state === 'sending';
  const canSubmit = !emailError && !messageError && !tooLong && !sending;

  async function submit(event: React.FormEvent) {
    event.preventDefault();

    setShowErrors(true);
    // Guards against a duplicate submission as well as an invalid one: a second submit
    // while `sending` is true cannot get past here.
    if (!canSubmit) return;

    setStatus({ state: 'sending' });

    try {
      await sendSupportMessage({ email: trimmedEmail, subject, message: trimmedMessage });
      setStatus({ state: 'sent' });
    } catch {
      // Whatever went wrong, the customer gets one sentence. The server has already
      // stripped provider detail; this does not go looking for more.
      setStatus({ state: 'failed', reason: FAILURE_MESSAGE });
    }
  }

  return (
    <div
      className="mm-modal-backdrop"
      // A click on the backdrop closes, as long as nothing is in flight. The check that
      // the click landed on the backdrop itself keeps a drag inside the dialog from
      // dismissing it.
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !sending) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        className="mm-modal"
        data-testid="support-modal"
      >
        {status.state === 'sent' ? (
          <div data-testid="support-sent">
            <h2 id={headingId} className="mm-section-title">
              Message sent
            </h2>
            <p className="mm-lede mt-3">
              {/* No response time is promised: nothing Sidney has supplied establishes one. */}
              Thanks for contacting Metered Mining. We&rsquo;ll get back to you as soon as
              possible.
            </p>

            <div className="mt-7 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                data-testid="support-close"
                className="mm-btn-primary"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} noValidate>
            <h2 id={headingId} className="mm-section-title">
              Contact Support
            </h2>
            <p className="mm-lede mt-3">
              Need help with an order or have a question? Send us a message.
            </p>

            <div className="mt-6">
              <label htmlFor={emailId} className="mm-label text-sm">
                Email address
              </label>
              <input
                id={emailId}
                type="email"
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={sending}
                aria-invalid={showErrors && emailError ? true : undefined}
                aria-describedby={showErrors && emailError ? `${emailId}-error` : undefined}
                className="mm-input mt-2 w-full"
                data-testid="support-email"
              />
              {showErrors && emailError && (
                <p
                  id={`${emailId}-error`}
                  role="alert"
                  className="mm-help mt-2 text-xs"
                  style={{ color: 'var(--mm-danger)' }}
                  data-testid="support-email-error"
                >
                  {emailError}
                </p>
              )}
            </div>

            <div className="mt-5">
              <label htmlFor={subjectId} className="mm-label text-sm">
                Subject
              </label>
              <select
                id={subjectId}
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                disabled={sending}
                className="mm-input mt-2 w-full"
                data-testid="support-subject"
              >
                {SUPPORT_SUBJECTS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="mt-5">
              <label htmlFor={messageId} className="mm-label text-sm">
                Message
              </label>
              <textarea
                id={messageId}
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                disabled={sending}
                maxLength={SUPPORT_MESSAGE_MAX_LENGTH}
                aria-invalid={showErrors && messageError ? true : undefined}
                aria-describedby={showErrors && messageError ? `${messageId}-error` : undefined}
                className="mm-textarea mt-2 w-full"
                data-testid="support-message"
              />
              {showErrors && messageError && (
                <p
                  id={`${messageId}-error`}
                  role="alert"
                  className="mm-help mt-2 text-xs"
                  style={{ color: 'var(--mm-danger)' }}
                  data-testid="support-message-error"
                >
                  {messageError}
                </p>
              )}
            </div>

            {status.state === 'failed' && (
              <p
                role="alert"
                className="mm-note mm-note--danger mt-5"
                data-testid="support-error"
              >
                <span>{status.reason}</span>
              </p>
            )}

            {/*
              TWO ACTIONS WITH A DELIBERATE HIERARCHY.

              THE CAUSE OF THE OLD MESS: `.mm-btn-primary` carries `w-full`, so inside a
              `flex-wrap` row "Send message" claimed the whole line and wrapped, leaving
              Cancel stranded on a row of its own, right-aligned above the primary. It
              read as an accident because it was one.

              DESKTOP: a single right-aligned row, secondary then primary -- the ordinary
              dialog convention, confirm nearest the corner the eye finishes on.

              MOBILE: stacked and full width, with the PRIMARY ON TOP. `flex-col-reverse`
              gets that from the same DOM order, so the tab order stays Cancel then Send
              on both -- the reversal is visual only, and the send action is the one under
              the thumb.
            */}
            <div className="mt-7 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={sending}
                className="mm-btn-secondary w-full sm:w-auto"
                data-testid="support-cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={sending}
                className="mm-btn-primary w-full sm:w-auto"
                data-testid="support-submit"
              >
                {sending ? 'Sending…' : 'Send message'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
